import 'react-image-crop/dist/ReactCrop.css';

import React, { useCallback, useRef, useState } from 'react';
import type { Crop } from 'react-image-crop';
import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop';

import { JWTAuthorization } from '../../../utils/JWTHeader';
import FileSelector from '../FileSelector/FileSelector';
import { showToast } from '../Notification';
import Spinner from '../Spinner';

interface SquareImageUploaderProps {
  currentImage?: string | null;
  uploadUrl: string;
  jwt: string | null;
  /**
   * Extract the stored image path from the server response JSON.
   * Example: (data) => data.brandingSettings?.brandingBlogImageName
   */
  extractPath: (response: unknown) => string | undefined;
  onUploaded: (path: string) => void;
  previewAlt: string;
  previewCaption?: string;
  previewSizeClass?: string;
  helperText?: React.ReactNode;
  disabled?: boolean;
}

const SQUARE_TOLERANCE_PX = 2;

const centerAspectCrop = (mediaWidth: number, mediaHeight: number, aspect: number) => centerCrop(
  makeAspectCrop({ unit: '%', width: 90 }, aspect, mediaWidth, mediaHeight),
  mediaWidth,
  mediaHeight,
);

const SquareImageUploader: React.FC<SquareImageUploaderProps> = ({
  currentImage,
  uploadUrl,
  jwt,
  extractPath,
  onUploaded,
  previewAlt,
  previewCaption,
  previewSizeClass = 'h-24 w-24',
  helperText,
  disabled = false,
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const [showCropper, setShowCropper] = useState(false);
  const [cropImage, setCropImage] = useState<string | null>(null);
  const [crop, setCrop] = useState<Crop | undefined>();
  const [completedCrop, setCompletedCrop] = useState<Crop | null>(null);
  const [tempFile, setTempFile] = useState<File | null>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  const uploadFile = useCallback(async(file: File) => {
    setIsSaving(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await fetch(uploadUrl, {
        method: 'POST',
        headers: { Authorization: JWTAuthorization(jwt) },
        body: formData,
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error((errorData && errorData.errors && errorData.errors.file) || 'Échec du téléchargement');
      }
      const data = await response.json();
      const path = extractPath(data);
      if (!path) {
        throw new Error('Chemin de l\'image manquant dans la réponse');
      }
      onUploaded(path);
      showToast('success', 'Logo enregistré.');
    } catch (err) {
      console.error(err);
      showToast('error', err instanceof Error ? err.message : 'Échec du téléchargement du logo');
    } finally {
      setIsSaving(false);
    }
  }, [uploadUrl, jwt, extractPath, onUploaded]);

  const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;
    setCrop(centerAspectCrop(width, height, 1));
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) { return; }
    const imageUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const isSquare = Math.abs(img.width - img.height) <= SQUARE_TOLERANCE_PX;
      if (isSquare) {
        URL.revokeObjectURL(imageUrl);
        void uploadFile(file);
      } else {
        setTempFile(file);
        setCropImage(imageUrl);
        setShowCropper(true);
      }
    };
    img.onerror = () => {
      setTempFile(file);
      setCropImage(imageUrl);
      setShowCropper(true);
      showToast('warning', 'Dimensions inconnues. Recadrage requis.');
    };
    img.src = imageUrl;
  };

  const handleCancelCrop = () => {
    setShowCropper(false);
    if (cropImage) { URL.revokeObjectURL(cropImage); }
    setCropImage(null);
    setTempFile(null);
    setCrop(undefined);
    setCompletedCrop(null);
  };

  const handleCropComplete = useCallback(async() => {
    if (!completedCrop || !imgRef.current || !tempFile) { return; }
    const canvas = document.createElement('canvas');
    const scaleX = imgRef.current.naturalWidth / imgRef.current.width;
    const scaleY = imgRef.current.naturalHeight / imgRef.current.height;
    const pixelRatio = window.devicePixelRatio;
    canvas.width = completedCrop.width * scaleX * pixelRatio;
    canvas.height = completedCrop.height * scaleY * pixelRatio;
    const ctx = canvas.getContext('2d');
    if (!ctx) { return; }
    ctx.scale(pixelRatio, pixelRatio);
    ctx.imageSmoothingQuality = 'high';
    const cropX = completedCrop.x * scaleX;
    const cropY = completedCrop.y * scaleY;
    const cropWidth = completedCrop.width * scaleX;
    const cropHeight = completedCrop.height * scaleY;
    ctx.drawImage(imgRef.current, cropX, cropY, cropWidth, cropHeight, 0, 0, cropWidth, cropHeight);
    canvas.toBlob(async(blob) => {
      if (!blob) { showToast('error', 'Erreur lors du recadrage'); return; }
      const croppedFile = new File([blob], tempFile.name, { type: tempFile.type, lastModified: Date.now() });
      await uploadFile(croppedFile);
      setShowCropper(false);
      if (cropImage) { URL.revokeObjectURL(cropImage); }
      setCropImage(null);
      setTempFile(null);
      setCrop(undefined);
      setCompletedCrop(null);
    }, tempFile.type);
  }, [completedCrop, tempFile, cropImage, uploadFile]);

  return (
    <div className="space-y-4">
      {currentImage && (
        <div className="flex flex-col items-center gap-2">
          <img
            src={currentImage}
            alt={previewAlt}
            className={`${previewSizeClass} rounded border object-cover shadow-sm`}
          />
          {previewCaption && <p className="text-sm text-gray-500">{previewCaption}</p>}
        </div>
      )}
      {helperText && (
        <p className="text-sm text-gray-500">{helperText}</p>
      )}
      <FileSelector
        accept="image/jpeg, image/png"
        onChange={handleFileChange}
        disabled={disabled || isSaving}
      />
      {isSaving && (
        <div className="flex items-center text-sm text-gray-500">
          <Spinner size="sm" className="mr-2" />Sauvegarde…
        </div>
      )}

      {showCropper && cropImage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-2xl rounded-lg bg-white p-6 shadow-xl">
            <h4 className="mb-4 text-lg font-semibold">Recadrer le logo</h4>
            <p className="mb-3 text-sm text-gray-600">
              Le logo du blog doit être carré. Ajustez la zone à conserver.
            </p>
            <div className="mb-4 max-h-[60vh] overflow-auto">
              <ReactCrop
                crop={crop}
                onChange={(c) => setCrop(c)}
                onComplete={(c) => setCompletedCrop(c)}
                aspect={1}
              >
                <img ref={imgRef} src={cropImage} alt="À recadrer" onLoad={onImageLoad} className="max-w-full" />
              </ReactCrop>
            </div>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={handleCancelCrop}
                className="rounded-lg border border-gray-300 px-4 py-2 hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleCropComplete}
                disabled={!completedCrop || isSaving}
                className="rounded-lg bg-rgpd-blue px-4 py-2 text-white disabled:opacity-50"
              >
                Appliquer le recadrage
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SquareImageUploader;
